import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Globe, MapPin, ChevronDown, ChevronRight } from "lucide-react";

export default function AdminZones() {
  const [mode, setMode] = useState("all");
  const [regions, setRegions] = useState([]); // [{key,label,departments:[{code,label}]}]
  const [openDeps, setOpenDeps] = useState([]); // ["75","92",...]
  const [expanded, setExpanded] = useState({}); // {IDF:true}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/zones");
      setMode(data.zones_mode);
      setRegions(data.regions || []);
      setOpenDeps(data.open_departments || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const depsOf = (r) => (r.departments || []).map((d) => d.code);
  const isRegionFull = (r) => depsOf(r).length > 0 && depsOf(r).every((c) => openDeps.includes(c));
  const regionOpenCount = (r) => depsOf(r).filter((c) => openDeps.includes(c)).length;

  const toggleRegion = (r) => {
    const codes = depsOf(r);
    setOpenDeps((cur) => {
      const full = codes.every((c) => cur.includes(c));
      if (full) return cur.filter((c) => !codes.includes(c));
      return Array.from(new Set([...cur, ...codes]));
    });
  };

  const toggleDept = (code) => {
    setOpenDeps((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));
  };

  const toggleExpand = (key) => setExpanded((cur) => ({ ...cur, [key]: !cur[key] }));

  const save = async () => {
    try {
      await api.put("/admin/zones", {
        zones_mode: mode,
        open_departments: mode === "regions" ? openDeps : [],
      });
      toast.success("Zones enregistrées.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const totalOpen = openDeps.length;

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Zones d&apos;attaque</h1>
        <p className="mt-2 max-w-2xl text-neutral-500">
          Ouvrez des régions entières, ou affinez au <strong>département</strong>. En mode restreint, seuls
          les candidats dont le code postal appartient à un département ouvert peuvent envoyer le formulaire.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => setMode("all")} data-testid="zones-mode-all"
            className={`inline-flex items-center gap-2 rounded-xl border px-5 py-4 text-left ${mode === "all" ? "border-brand bg-brand/5" : "border-neutral-200 bg-white hover:border-neutral-400"}`}>
            <Globe className="h-5 w-5 text-brand" />
            <span><span className="block font-bold">Tout le pays</span><span className="block text-xs text-neutral-500">Aucune restriction géographique</span></span>
          </button>
          <button onClick={() => setMode("regions")} data-testid="zones-mode-regions"
            className={`inline-flex items-center gap-2 rounded-xl border px-5 py-4 text-left ${mode === "regions" ? "border-brand bg-brand/5" : "border-neutral-200 bg-white hover:border-neutral-400"}`}>
            <MapPin className="h-5 w-5 text-brand" />
            <span><span className="block font-bold">Zones choisies</span><span className="block text-xs text-neutral-500">Régions et/ou départements</span></span>
          </button>
        </div>

        {mode === "regions" && (
          <>
            <p className="mt-6 text-sm text-neutral-500" data-testid="zones-open-count">
              {totalOpen === 0 ? "Aucun département ouvert pour l'instant." : `${totalOpen} département(s) ouvert(s).`}
            </p>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              {regions.map((r) => {
                const full = isRegionFull(r);
                const cnt = regionOpenCount(r);
                const total = depsOf(r).length;
                const partial = cnt > 0 && !full;
                return (
                  <div key={r.key} className="rounded-xl border border-neutral-200 bg-white" data-testid={`zone-region-card-${r.key}`}>
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <button
                        onClick={() => toggleExpand(r.key)}
                        data-testid={`zone-region-toggle-${r.key}`}
                        className="flex items-center gap-2 text-left text-sm font-bold text-neutral-900"
                      >
                        {expanded[r.key] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {r.label}
                        <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${full ? "bg-brand text-white" : partial ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-500"}`}>
                          {full ? "Toute la région" : partial ? `${cnt}/${total}` : "Fermée"}
                        </span>
                      </button>
                      <button
                        onClick={() => toggleRegion(r)}
                        data-testid={`zone-region-${r.key}`}
                        className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${full ? "bg-neutral-900 text-white hover:bg-red-600" : "border border-brand text-brand hover:bg-brand hover:text-white"}`}
                      >
                        {full ? "Tout fermer" : "Toute la région"}
                      </button>
                    </div>

                    {expanded[r.key] && (
                      <div className="flex flex-wrap gap-2 border-t border-neutral-100 px-4 py-3">
                        {(r.departments || []).map((d) => {
                          const on = openDeps.includes(d.code);
                          return (
                            <button
                              key={d.code}
                              onClick={() => toggleDept(d.code)}
                              data-testid={`zone-dept-${d.code}`}
                              title={d.label}
                              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-brand text-white" : "border border-neutral-300 text-neutral-600 hover:border-neutral-900"}`}
                            >
                              {d.code} · {d.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <button onClick={save} data-testid="zones-save"
          className="mt-8 rounded-md bg-brand px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90">
          Enregistrer les zones
        </button>
      </div>
    </Shell>
  );
}
